
import React, { useEffect } from 'react';
import Layout from '@/components/Layout/Layout';
import DebtCalculator from '@/components/SpecialCalculators/FinancialCalculators/DebtCalculator';
import { Link } from 'react-router-dom';

const DebtCalculatorPage = () => {
  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4">
        {/* Header Section */}
        <div className="mb-8">
          <div className="text-sm text-gray-500 mb-2">
            <Link to="/" className="hover:underline">Home</Link> {' > '} 
            <Link to="/special" className="hover:underline">Special Tools</Link> {' > '} 
            <span>Debt Payoff Calculator</span>
          </div>
          <h1 className="text-3xl font-bold mb-3">Debt Payoff Calculator</h1>
          <p className="text-gray-700">
            Create a debt payoff plan, compare payoff strategies, and see how quickly you can become debt-free.
          </p>
        </div>

        {/* Calculator Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <DebtCalculator />
        </div>

        {/* Information Section */}
        <div className="bg-[#E6F3FF] p-6 rounded-lg mb-8">
          <h2 className="text-2xl font-semibold mb-3">How to use the Debt Payoff Calculator</h2>
          <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-2">
            <li>Enter information about each of your debts (name, balance, interest rate, minimum payment)</li>
            <li>Add or remove debts as needed to match your situation</li>
            <li>Enter your monthly payment amount (must be at least equal to the sum of all minimum payments)</li>
            <li>Choose your preferred payoff method (debt avalanche or debt snowball)</li>
            <li>Calculate your debt payoff plan and review the results</li>
          </ol>
        </div>

        {/* Additional Information */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Debt Payoff Strategies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-xl font-medium">Debt Avalanche</h3>
              <p className="text-gray-700">
                The debt avalanche method focuses on paying off debts in order of highest interest rate to lowest. This approach:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-3">
                <li>Minimizes the total interest paid</li>
                <li>Is mathematically optimal for saving money</li>
                <li>May take longer to eliminate individual debts</li>
                <li>Works best for those motivated by long-term financial efficiency</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h3 className="text-xl font-medium">Debt Snowball</h3>
              <p className="text-gray-700">
                The debt snowball method focuses on paying off debts in order of smallest balance to largest. This approach:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-3">
                <li>Provides quick wins by eliminating smaller debts first</li>
                <li>Builds psychological momentum and motivation</li>
                <li>May result in paying more total interest</li>
                <li>Works best for those who need motivational victories</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-md">
            <h3 className="text-lg font-medium mb-2">Additional Tips for Debt Payoff</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700 ml-3">
              <li>Always pay at least the minimum amount on all debts to avoid fees and credit damage</li>
              <li>Consider balance transfers or debt consolidation for high-interest debts</li>
              <li>Look for ways to increase income or reduce expenses to accelerate debt payoff</li>
              <li>Once a debt is paid off, roll that payment into the next debt (this is the "snowball" effect)</li>
              <li>Create an emergency fund to avoid taking on new debt for unexpected expenses</li>
            </ul>
          </div>
        </div>

        {/* Related Calculators */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold mb-3">Related Calculators</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/special/loan" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💰</span> Loan Calculator
            </Link>
            <Link to="/special/interest" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">💲</span> Interest Calculator
            </Link>
            <Link to="/special/mortgage" className="p-4 border border-gray-200 rounded-md hover:bg-gray-50 flex items-center">
              <span className="text-xl mr-2">🏠</span> Mortgage Calculator
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DebtCalculatorPage;
